import React, { Activity, useCallback } from 'react';
import { FolderManager } from './FolderManager';
import { useFolderManagerActions } from '../../hooks/useFolderManagerActions';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerBreadcrumb,
  FolderManagerMimeTypes,
  FolderManagerViewTypes,
} from '../../interfaces';
import { FolderActionsConfig } from './Components/FolderManagerFoldersView/components/FolderRow';
import { SlidesViewFilters } from './Components/FolderManagerSlidesViewFilters';
import { UploadNewAssetProvider, useUploadNewAsset } from './UploadNewAssetContext';
import { UploadNewAsset } from './Components/UploadNewAsset';
import { UploadNewAssetVersion } from './Components/UploadNewAssetVersion';

interface FolderManagerWithActionsProps {
  isLoading: boolean;
  fileInputRef?: any;
  rootFolderId: number;
  noDataMessage?: string;
  selectedFolderId?: any;
  selectedFolderData?: any;
  slug?: string;
  rootFolder?: any;
  viewStateKey?: string;
  availableViews?: (FolderManagerViewTypes.SLIDES | FolderManagerViewTypes.GRID | FolderManagerViewTypes.LIST | FolderManagerViewTypes.FOLDERS)[];
  selectedKeys?: Set<string>;
  enableZoomControls?: boolean;
  isSlidesFolder?: boolean;
  showThemePicker?: boolean;
  slideRepositoryId?: number;
  filters?: SlidesViewFilters;
  onFiltersChange?: (filters: SlidesViewFilters) => void;
  isPresentationThemesFolder?: boolean;
  noCard?: boolean;
  disableSelectAll?: boolean;
  enableGroupByPptx?: boolean;

  hierarchyData: FolderManagerItem[];
  breadcrumbs: FolderManagerBreadcrumb[];
  disableUpload?: boolean;
  defaultViewType?: FolderManagerViewTypes;
  newButtonActionType?: string | null;
  minHeight?: string;
  acceptedDragTypes?: FolderManagerMimeTypes[];
  stickyHeaderTop?: number;

  onClearNewButtonAction: () => void;
  customMultiSelectActions?: (data: {
    selectedItems: any[];
    onClear: () => void;
    onAction?: FolderManagerAction;
  }) => void;
  onAction?: (data: FolderManagerAction) => void;
  onSelect?: (data: { selectedRows: FolderManagerItem[]; clearSelection: () => void }) => void;
  allowEntityClick?: boolean;
  disableActionColumn?: boolean;
  disableInternalDnD?: boolean;
  enableDnD?: boolean;
  controlledSelectedItems?: FolderManagerItem[];

  customActionHandlers?: Partial<
    Record<FolderManagerActionTypes, (action: FolderManagerAction) => void | Promise<void>>
  >;
  onResultCount?: (count: number) => void;
  onActionSuccess?: (actionType: FolderManagerActionTypes) => void;
  onActionError?: (actionType: FolderManagerActionTypes, error: any) => void;
  actionsConfig?: FolderActionsConfig;
  /** Enables compact grid layout (many small columns, tight gap) and compact item rendering */
  compactGrid?: boolean;
  compactGridMaxColumns?: number;
  hideMultiSelectBar?: boolean;
  hideDetailsChevron?: boolean;
  subHeaderContent?: React.ReactNode;
  breadcrumbMaxLength?: number;
}

export const FolderManagerWithActions: React.FC<FolderManagerWithActionsProps> = ({
  slug,
  selectedFolderId,
  selectedFolderData,
  rootFolder,
  customActionHandlers,
  onAction,
  onActionSuccess,
  onActionError,
  ...folderManagerProps
}) => {
  const isSlidesFolder = folderManagerProps.isSlidesFolder || selectedFolderData?.isSlideRepositoryFolder;

  const { handleAction, dialogs } = useFolderManagerActions({
    slug,
    selectedFolderId,
    selectedFolderData,
    rootFolder,
    isSlidesFolder,
    customActionHandlers,
    onActionSuccess,
    onActionError,
  });

  const combinedActionHandler = useCallback(
    (action: FolderManagerAction) => {
      handleAction(action);

      if (onAction) {
        onAction(action);
      }
    },
    [handleAction, onAction],
  );

  return (
    <UploadNewAssetProvider
      rootFolderId={folderManagerProps.rootFolderId ?? null}
      viewStateKey={folderManagerProps.viewStateKey}
    >
      <UploadOverlays />
      <FolderManagerVisibilityGate>
        <FolderManager
          {...folderManagerProps}
          isSlidesFolder={isSlidesFolder}
          enableZoomControls={folderManagerProps.enableZoomControls ?? isSlidesFolder}
          selectedFolderId={selectedFolderId}
          onAction={combinedActionHandler}
        />
      </FolderManagerVisibilityGate>
      {dialogs}
    </UploadNewAssetProvider>
  );
};

const UploadOverlays: React.FC = () => {
  const { pendingUpload, pendingVersionUpload } = useUploadNewAsset();

  return (
    <>
      {pendingUpload && <UploadNewAsset key={pendingUpload.uploadId} />}
      {pendingVersionUpload && <UploadNewAssetVersion key={pendingVersionUpload.uploadId} />}
    </>
  );
};

const FolderManagerVisibilityGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pendingUpload, pendingVersionUpload } = useUploadNewAsset();
  const isUploadPending = !!pendingUpload || !!pendingVersionUpload;

  return <Activity mode={isUploadPending ? 'hidden' : 'visible'}>{children}</Activity>;
};
